import { Controller } from "@hotwired/stimulus"

// Formularz reguły automatyzacji: pokazuje sekcje właściwe dla wybranego triggera
// (event/schedule/time_offset/segment); pola ukrytych sekcji są disabled, żeby nie
// wysyłały wartości. Dla triggera segment pobiera podgląd liczby wyników filtra.
export default class extends Controller {
  static targets = ["triggerKind", "eventSection", "scheduleSection", "timeOffsetSection",
    "segmentSection", "segmentSelect", "segmentCount", "hoursSection",
    "timeOffsetSource", "anchorWrap", "eventSource", "eventSelect", "conditionsFrame",
    "conditionsInput"]

  static values = { countUrl: String, resultsLabel: String, conditionsUrl: String, events: Object }

  connect() {
    this.toggle()
    // builder warunków jest wyrenderowany inline dla stanu początkowego -
    // przeładowania frame'a tylko przy zmianach dokonanych przez użytkownika
    this.conditionsReady = true
  }

  toggle() {
    const trigger = this.triggerKindTarget.value
    this.toggleSection(this.eventSectionTarget, trigger === "event")
    this.toggleSection(this.scheduleSectionTarget, trigger === "schedule")
    this.toggleSection(this.timeOffsetSectionTarget, trigger === "time_offset")
    // toggleSection włącza WSZYSTKIE pola pokazanej sekcji - selecty anchora (jeden per model)
    // trzeba od razu zawęzić do wybranego modelu, inaczej poszłoby kilka rule[anchor_field]
    if (trigger === "time_offset") this.toggleAnchor()
    if (this.hasSegmentSectionTarget) {
      this.toggleSection(this.segmentSectionTarget, trigger === "segment")
      if (trigger === "segment") this.refreshSegmentCount()
    }
    // okno godzin tylko dla event/time_offset - przy schedule/segment KIEDY ustala harmonogram,
    // a okno byłoby pułapką (np. harmonogram 9:00 + okno 10-12 = reguła nigdy nie działa)
    if (this.hasHoursSectionTarget) {
      this.toggleSection(this.hoursSectionTarget, trigger === "event" || trigger === "time_offset")
    }
    // zmiana triggera zmienia model źródłowy i sens operatorów (changed tylko przy zdarzeniach)
    this.conditionsChanged()
  }

  toggleSection(section, visible) {
    section.hidden = !visible
    section.querySelectorAll("select, input, textarea").forEach((el) => {
      el.disabled = !visible
    })
  }

  // Przeładowuje builder warunków (turbo-frame) po zmianie triggera/modelu źródłowego -
  // lista pól i operatory (changed tylko dla zdarzeń) zależą od wyboru. Bieżące warunki
  // jadą w parametrze, żeby przełączenie nie kasowało zbudowanych wierszy.
  conditionsChanged() {
    if (!this.conditionsReady || !this.hasConditionsFrameTarget || !this.conditionsUrlValue) return

    const trigger = this.triggerKindTarget.value
    const url = new URL(this.conditionsUrlValue, window.location.origin)
    url.searchParams.set("trigger_kind", trigger)
    const source = this.conditionsSourceType(trigger)
    if (source) url.searchParams.set("source_type", source)
    const current = this.hasConditionsInputTarget ? this.conditionsInputTarget.value : null
    if (current) url.searchParams.set("conditions", current)
    this.conditionsFrameTarget.src = url.toString()
  }

  // Model źródłowy dla warunków zależnie od triggera (przy segmencie z wybranego filtra)
  conditionsSourceType(trigger) {
    if (trigger === "event" && this.hasEventSourceTarget) return this.eventSourceTarget.value
    if (trigger === "time_offset" && this.hasTimeOffsetSourceTarget) return this.timeOffsetSourceTarget.value
    if (trigger === "segment" && this.hasSegmentSelectTarget) {
      return this.segmentSelectTarget.selectedOptions[0]?.dataset.subjectType || null
    }
    return null
  }

  // Pokazuje select "Pole daty rekordu" pasujący do wybranego modelu źródłowego
  // (lista pól daty zależy od modelu); pozostałe selecty disabled, żeby nie wysyłały wartości
  toggleAnchor() {
    if (!this.hasTimeOffsetSourceTarget) return
    const model = this.timeOffsetSourceTarget.value
    this.anchorWrapTargets.forEach((wrap) => {
      const visible = wrap.dataset.anchorModel === model
      wrap.hidden = !visible
      wrap.querySelectorAll("select").forEach((el) => {
        el.disabled = !visible
      })
    })
  }

  // Zdarzenia zależą od modelu źródłowego (Mail::Email: receive/send/update; pozostałe:
  // create/update) - przebudowa selecta zdarzenia po zmianie modelu. Zachowuje bieżącą
  // wartość jeśli nadal dostępna dla nowego modelu.
  sourceTypeChanged() {
    if (!this.hasEventSourceTarget || !this.hasEventSelectTarget) return
    const options = this.eventsValue[this.eventSourceTarget.value] || []
    const current = this.eventSelectTarget.value
    this.eventSelectTarget.innerHTML = ""
    options.forEach(([label, value]) => {
      const option = document.createElement("option")
      option.value = value
      option.textContent = label
      if (value === current) option.selected = true
      this.eventSelectTarget.appendChild(option)
    })
  }

  // Podgląd liczby wyników wybranego filtra (pełne wyszukiwanie: pola + warunki) -
  // user widzi skalę zanim zapisze regułę
  async refreshSegmentCount() {
    if (!this.hasSegmentSelectTarget || !this.hasSegmentCountTarget || !this.countUrlValue) return
    const id = this.segmentSelectTarget.value
    this.segmentCountTarget.textContent = ""
    if (!id) return

    // anuluj poprzedni fetch - spóźniona odpowiedź nie nadpisze liczby aktualnego filtra
    this.countAbort?.abort()
    this.countAbort = new AbortController()

    try {
      const res = await fetch(`${this.countUrlValue}.json?segment_id=${encodeURIComponent(id)}`,
        { headers: { Accept: "application/json" }, signal: this.countAbort.signal })
      if (res.ok) {
        const data = await res.json()
        if (data.count != null) this.segmentCountTarget.textContent = `≈ ${data.count} ${this.resultsLabelValue} · `
      }
    } catch { /* podgląd liczby to dodatek - brak nie blokuje formularza */ }
  }
}
